import { useEffect, useState } from "react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, Send, ClipboardList, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { TierBadge, CATEGORY_LABEL } from "@/components/network/TierBadge";
import { Link } from "react-router-dom";

const TIER_KINDS = {
  bronze:     ["service_recorded"],
  silver:     ["service_recorded", "insurance_recorded"],
  gold:       ["service_recorded", "insurance_recorded", "accident_reported"],
  platinum:   ["service_recorded", "insurance_recorded", "accident_reported", "ownership_transferred"],
  government: ["service_recorded", "insurance_recorded", "accident_reported", "ownership_transferred", "duty_confirmed", "scrapped"],
};

export default function PartnerPortal() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [form, setForm] = useState({
    kind: "service_recorded", asset_id: "", title: "", detail: "",
    mileage_km: "", cost_usd: "", reference_number: "",
  });

  const loadOrgs = async () => {
    const r = await api.get("/network/mine");
    setOrgs(r.data);
    if (r.data[0]) setSelected(r.data[0]);
  };
  const loadUpdates = async (org) => {
    if (!org) return;
    const r = await api.get(`/network/organisations/${org.id}/updates`);
    setUpdates(r.data);
  };
  useEffect(() => { loadOrgs(); }, []);
  useEffect(() => { loadUpdates(selected); }, [selected]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) { toast.error("Select an organisation"); return; }
    try {
      await api.post(`/network/organisations/${selected.id}/updates`, {
        kind: form.kind,
        asset_id: form.asset_id.trim(),
        title: form.title,
        detail: form.detail,
        mileage_km: form.mileage_km ? Number(form.mileage_km) : null,
        cost_usd: form.cost_usd ? Number(form.cost_usd) : null,
        reference_number: form.reference_number || null,
      });
      toast.success("Trusted update posted to the Digital Passport");
      setForm({ ...form, asset_id: "", title: "", detail: "", mileage_km: "", cost_usd: "", reference_number: "" });
      loadUpdates(selected);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to post update");
    }
  };

  const allowedKinds = selected ? (TIER_KINDS[selected.tier] || ["service_recorded"]) : [];

  return (
    <div>
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="kicker">Trust Network · Partner Portal</div>
            <h1 className="display text-4xl">Post Trusted Updates</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Verified organisations record lifecycle events directly into the Digital Passport™ of every asset.
            </p>
          </div>
          <Link to="/network/register"><Button variant="outline" data-testid="btn-register-org">Register another organisation</Button></Link>
        </div>

        {orgs.length === 0 ? (
          <div className="border border-border p-10 bg-card text-center">
            <Building2 className="h-10 w-10 mx-auto text-muted-foreground" />
            <div className="display text-2xl mt-3">You aren't a member of any organisation yet</div>
            <p className="text-muted-foreground text-sm mt-1">Register your organisation to join the Trust Network.</p>
            <Link to="/network/register"><Button className="mt-4" data-testid="btn-cta-register-org">Register organisation</Button></Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[300px_1fr] gap-6">
            {/* Left: my organisations */}
            <aside className="space-y-2">
              <div className="kicker">Your organisations</div>
              {orgs.map(o => (
                <button
                  key={o.id}
                  onClick={() => setSelected(o)}
                  data-testid={`select-org-${o.id}`}
                  className={`w-full text-left border p-3 rounded-sm ${selected?.id === o.id ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"}`}
                >
                  <div className="text-sm font-semibold">{o.name}</div>
                  <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground mt-1">
                    {CATEGORY_LABEL[o.category]} · {o.membership_role}
                  </div>
                  <div className="mt-2"><TierBadge tier={o.tier} /></div>
                  <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground mt-2">
                    {o.status === "verified" ? "Accredited" : "Pending verification"}
                  </div>
                </button>
              ))}
            </aside>

            {/* Right: post + history */}
            <div className="space-y-6">
              {selected && selected.status !== "verified" && (
                <div className="border border-amber-400/50 bg-amber-50 text-amber-900 p-4 rounded-sm text-sm" data-testid="pending-verify-banner">
                  Your organisation is pending verification. You can prepare updates but cannot post to the Passport until accreditation is granted.
                </div>
              )}
              <form onSubmit={submit} className="border border-border p-6 bg-card space-y-4" data-testid="post-update-form">
                <div className="flex items-center gap-2">
                  <Send className="h-4 w-4 text-primary" />
                  <div className="display text-xl">Record a Trusted Update</div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs mono uppercase tracking-widest">Update kind</Label>
                    <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                      <SelectTrigger data-testid="update-kind-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {allowedKinds.map(k => <SelectItem key={k} value={k}>{k.replace(/_/g, " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs mono uppercase tracking-widest">Asset ID</Label>
                    <Input value={form.asset_id} onChange={(e) => setForm({ ...form, asset_id: e.target.value })} required placeholder="veh_…" data-testid="update-asset-id" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs mono uppercase tracking-widest">Title *</Label>
                    <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="10,000 km scheduled service" data-testid="update-title" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs mono uppercase tracking-widest">Detail</Label>
                    <Textarea rows={3} value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} data-testid="update-detail" />
                  </div>
                  <div><Label className="text-xs mono uppercase tracking-widest">Mileage (km)</Label><Input type="number" value={form.mileage_km} onChange={(e) => setForm({ ...form, mileage_km: e.target.value })} /></div>
                  <div><Label className="text-xs mono uppercase tracking-widest">Cost (USD)</Label><Input type="number" value={form.cost_usd} onChange={(e) => setForm({ ...form, cost_usd: e.target.value })} /></div>
                  <div className="md:col-span-2"><Label className="text-xs mono uppercase tracking-widest">Reference / invoice number</Label><Input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} /></div>
                </div>
                <div className="flex justify-end">
                  <Button type="submit" data-testid="btn-submit-update">Post update</Button>
                </div>
              </form>

              {/* History */}
              <div className="border border-border bg-card">
                <div className="p-4 border-b border-border flex items-center gap-2">
                  <ClipboardList className="h-4 w-4 text-primary" />
                  <div className="display text-lg">Recent trusted updates</div>
                </div>
                {updates.length === 0 ? (
                  <div className="p-6 mono text-sm text-muted-foreground">No updates yet.</div>
                ) : updates.map(u => (
                  <div key={u.id} className="p-4 border-b border-border last:border-0 flex items-start gap-4">
                    <FileText className="h-4 w-4 text-primary mt-1" />
                    <div className="flex-1">
                      <div className="text-xs mono uppercase tracking-widest text-muted-foreground">{u.kind.replace(/_/g, " ")} · {new Date(u.created_at).toLocaleDateString()}</div>
                      <div className="font-semibold mt-0.5">{u.title}</div>
                      {u.detail && <div className="text-sm text-muted-foreground mt-1">{u.detail}</div>}
                      <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground mt-2">Asset {u.asset_id}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
